import { NgStyle } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PlanType } from '@app/+authenticated/+reports/shared/subscriptions/subscription.model';
import { Features } from '@app/enums';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { TrackingService } from '@app/services/tracking.service';
import { FeatureService } from '@app/startup/feature.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DepartmentVariationApi } from '@reducers/orm/department-variation';
import {
  DEFAULT_SETTINGS_VARIATION_ID,
  DepartmentVariationSection,
  DepartmentVariationSettingModel,
} from '@reducers/orm/department-variation/department-variation.model';
import {
  DIALOG_DATA,
  DialogActivationComponent,
  DialogRef,
  InputModule,
  RadioBadgeComponent,
  RadioBadgeGroupComponent,
} from '@sb/ui';
import { BehaviorSubject, filter, finalize, map, switchMap, take } from 'rxjs';

import { OnboardingChapterModule } from '../components/chapter.module';
import { OptimizedScheduleDialogStep } from './optimized-schedule.model';

@Component({
  selector: 'optimized-schedule-dialog',
  standalone: true,
  imports: [
    DialogActivationComponent,
    FormsModule,
    NgStyle,
    TranslateModule,
    OnboardingChapterModule,
    InputModule,
    RadioBadgeComponent,
    RadioBadgeGroupComponent,
  ],
  providers: [PlanCheckPipe],
  templateUrl: './optimized-schedule-dialog.component.html',
})
export class OptimizedScheduleDialogComponent {
  public OptimizedScheduleDialogStep = OptimizedScheduleDialogStep;
  public currentStep = OptimizedScheduleDialogStep.TEASER;
  public steps = 2;

  public availabilityWeekAmount = '1';
  public availabilityWeekAmountOptions: { value: string; label: string }[] = [
    { value: '1', label: this.translate.instant('1 week') },
    { value: '2', label: this.translate.instant('2 weeks') },
    { value: '3', label: this.translate.instant('3 weeks') },
    { value: '4', label: this.translate.instant('4 weeks') },
  ];

  public nextButtonLoading = false;

  public hasPremium: boolean;

  private scheduleAvailabilitySettings: Partial<DepartmentVariationSettingModel>;
  private settingsLoaded$ = new BehaviorSubject<boolean>(false);
  private eventTracked = false;

  public constructor(
    @Inject(DIALOG_DATA) public readonly eventData: { originator: string },
    private readonly dialogRef: DialogRef,
    private readonly trackingService: TrackingService,
    private readonly departmentVariationAPI: DepartmentVariationApi,
    private readonly planCheck: PlanCheckPipe,
    private readonly translate: TranslateService,
    private readonly featureService: FeatureService,
  ) {
    void this.getDefaultVariationSettings(DepartmentVariationSection.SCHEDULE_AVAILABILITY)
      .pipe(take(1))
      .subscribe((availabilitySettings) => {
        this.scheduleAvailabilitySettings = availabilitySettings;
        this.settingsLoaded$.next(true);
      });

    this.hasPremium = this.planCheck.transform(PlanType.PREMIUM);
    this.steps = this.hasPremium ? 3 : 2;
  }

  public goToStep(step: OptimizedScheduleDialogStep) {
    if (this.currentStep === OptimizedScheduleDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Optimized Scheduling Started', this.eventData);
      this.eventTracked = true;
    }

    if (step === OptimizedScheduleDialogStep.EMPLOYEE_AVAILABILITY && !this.hasPremium) {
      // Skip employee availability settings step for non-premium accounts
      this.dialogRef.close({ action: 'primary' });
      return;
    }

    this.currentStep = step;
  }

  public goBack() {
    this.currentStep = this.currentStep - 1;
  }

  public savePreferencesAndClose() {
    this.nextButtonLoading = true;

    void this.settingsLoaded$
      .pipe(
        filter((loaded) => loaded),
        switchMap(() => this.saveScheduleAvailabilitySettings()),
        take(1),
        finalize(() => {
          this.nextButtonLoading = false;
        }),
      )
      .subscribe(() => {
        this.dialogRef.close({ action: 'primary' });
      });
  }

  private saveScheduleAvailabilitySettings() {
    let scheduleAvailabilitySettings: Partial<DepartmentVariationSettingModel> = {};
    const availabilityWeekAmount = Number(this.availabilityWeekAmount);

    // todo: check if TMP_AVAILABILITY_OPTIMISATION is enabled (see #SHB-12898)
    if (this.featureService.isEnabled(Features.TMP_AVAILABILITY_OPTIMISATION)) {
      scheduleAvailabilitySettings = {
        ...this.scheduleAvailabilitySettings,
        availability_deadline: {
          type: 'week',
          amount: availabilityWeekAmount * 7,
        },
        availability_enabled: true,
        // send reminder 7 week after the deadline
        availability_reminder_days_before: 7,
        availability_send_availability_reminder: true,
      };
    } else {
      scheduleAvailabilitySettings = {
        ...this.scheduleAvailabilitySettings,
        lock_availability_days_before_period: String(availabilityWeekAmount * 7),
        reminder_days_before: String((availabilityWeekAmount + 1) * 7),
        send_availability_reminder: true,
      };
    }

    return this.departmentVariationAPI.add(
      scheduleAvailabilitySettings,
      DEFAULT_SETTINGS_VARIATION_ID,
      DepartmentVariationSection.SCHEDULE_AVAILABILITY,
    );
  }

  private getDefaultVariationSettings(section: DepartmentVariationSection) {
    return this.departmentVariationAPI.load(section).pipe(
      filter((variations) => variations?.length > 0),
      map((variations) => variations.find((variation) => variation.department_id === DEFAULT_SETTINGS_VARIATION_ID)),
      take(1),
    );
  }
}
